import * as React from "react";
import { AddressInput } from "kw/util/Question/inputs/AddressInput";
import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { createScript } from "kanopi-components/utils/browser";
import { CybersourceField } from "./CybersourceFields";
import { EmailInput } from "kw/util/Question/inputs/EmailInput";
import { ExpirationInput, FormControl, InputWrapper } from "kanopi-components/Input";
import { formatCurrency } from "kanopi-components/Currency";
import { handleErrorDisplay } from "kw/util/utils";
import { HeadingSmall, HeadingLarge } from "kanopi-components/typography";
import { IField } from "kanopi-components/useYupSchema/validatorRegistry";
import { Placeholder } from "kanopi-components/Placeholder";
import { StringInput } from "kw/util/Question/inputs/StringInput";
import { TPaymentFormProps } from "./types";
import { useCaptureContext } from "./useCaptureContext";
import { useForm } from "react-weaver";
import { usePayment } from "./usePayment";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useUser } from "kw/auth/useUser";
import { useYupSchema } from "kanopi-components/useYupSchema";
import "./cybersourceStyles.css";

function addTestId(id) {
  return {
    Block: {
      props: {
        "data-testid": id,
      },
    },
  };
}

const CybersourceForm: React.FC<TPaymentFormProps> = (props) => {
  const [loaded, setLoaded] = React.useState(false);
  const {
    captureContext,
    loading: loadingCaptureContext,
    getCaptureContext,
  } = useCaptureContext();

  React.useEffect(() => {
    createScript({
      src: "https://flex.cybersource.com/cybersource/assets/microform/0.11/flex-microform.min.js",
      onLoad: () => {
        setLoaded(true);
      },
    });
  }, []);

  return (
    <Placeholder loading={(!loaded || loadingCaptureContext) && !!captureContext}>
      <CybersourceInnerForm
        captureContext={captureContext}
        getCaptureContext={getCaptureContext}
        {...props}
      />
    </Placeholder>
  );
};

type TCybersourceInnerFormProps = {
  riskProfile: TRiskProfile;
  captureContext: string;
  getCaptureContext: () => void;
  onSubmit?: () => void;
};

const CybersourceInnerForm: React.FC<TCybersourceInnerFormProps> = ({
  riskProfile = {},
  captureContext,
  getCaptureContext,
  onSubmit,
}) => {
  const [, theme] = useStyletron();

  const user = useUser();
  const [csMicroForm, setCsMicroForm] = React.useState(null);

  const [cybersourceLoading, setCybersourceLoading] = React.useState(false);
  const {
    submit: paymentSubmit,
    loading: paymentLoading,
    disableRepayment,
  } = usePayment({
    riskProfileId: riskProfile.id,
    onSubmit,
  });

  const amount = riskProfile.periodicPrice;
  const readableAmount = amount ? `$${formatCurrency(amount)}` : "";
  const disableSubmit = cybersourceLoading || paymentLoading || disableRepayment;

  const [cardField, setCardField] = React.useState();
  const [cvcField, setCVCField] = React.useState();
  const [cardValid, setCardValid] = React.useState<boolean>();
  const [cvcValid, setCVCValid] = React.useState<boolean>();

  React.useEffect(() => {
    if (!captureContext) {
      return;
    }

    const csFlex = new Flex(captureContext);
    const microform = csFlex.microform({
      styles: {
        input: {
          "font-size": theme.sizing.scale600,
          "font-family": theme.typography.DisplayMedium.fontFamily,
          "font-weight": "normal",
          "line-height": theme.sizing.scale800,
        },
      },
    });
    const fieldNumber = microform.createField("number", {
      placeholder: "1234 1234 1234 1234",
    });
    const fieldSecurityCode = microform.createField("securityCode", {
      placeholder: "•••",
    });
    fieldNumber.load("#number-container");

    setCardField(fieldNumber);
    setCVCField(fieldSecurityCode);
    fieldSecurityCode.load("#cvc-container");

    setCsMicroForm(microform);
  }, [captureContext]);

  const getToken = async (
    expirationMonth: string,
    expirationYear: string
  ): Promise<{ error: unknown; transientToken: string }> => {
    return new Promise((resolve) => {
      csMicroForm.createToken(
        {
          expirationMonth,
          expirationYear,
        },
        (error, transientToken: string) => {
          resolve({
            error,
            transientToken,
          });
        }
      );
    });
  };

  const cybersourceSubmit = async (event) => {
    event.preventDefault();
    try {
      setCybersourceLoading(true);

      const [month, year] =
        fieldProps.expiration &&
        fieldProps.expiration.value &&
        fieldProps.expiration.value.split("/");
      const expirationMonth = ("00" + month).slice(-2);
      const expirationYear = "20" + year;

      const { error, transientToken } = await getToken(expirationMonth, expirationYear);
      if (error) {
        const captureContextErrors = [
          "CAPTURE_CONTEXT_INVALID",
          "CAPTURE_CONTEXT_EXPIRED",
          "CREATE_TOKEN_CAPTURE_CONTEXT_USED_TOO_MANY_TIMES",
          "CREATE_TOKEN_CAPTURE_CONTEXT_NOT_FOUND",
        ];
        if (captureContextErrors.includes(error.reason)) {
          handleErrorDisplay(
            error,
            "Something went wrong processing your payment. Please re-enter a valid " +
              "Visa or Mastercard and try again."
          );
          getCaptureContext();
        } else {
          handleErrorDisplay(
            error,
            "Something went wrong processing your payment. " +
              "Please check that your payment details are correct and that you are " +
              "using Visa or Mastercard, then try again."
          );
        }
      } else {
        paymentSubmit({
          transient_token: transientToken,
          expiration_month: expirationMonth,
          expiration_year: expirationYear,
          bill_to: {
            first_name: fieldProps?.firstName?.value,
            last_name: fieldProps?.lastName?.value,
            address: fieldProps?.billingAddress?.value?.formattedAddress,
            locality: fieldProps?.billingAddress?.value?.suburb,
            postal_code: fieldProps.billingAddress?.value?.postcode,
            country: "Australia",
            email: fieldProps?.email?.value,
            phone_number: fieldProps?.phoneNumber?.value,
          },
        });
      }
    } catch (e) {
      handleErrorDisplay(e);
    } finally {
      setCybersourceLoading(false);
    }
  };

  const fields: IField[] = [
    {
      id: "billingAddress",
      label: "Address",
      inputType: "address",
      validators: [
        {
          type: "RequiredValidator",
        },
        {
          type: "AddressValidator",
        },
      ],
    },
    {
      id: "firstName",
      label: "First name",
      inputType: "string",
      validators: [
        {
          type: "RequiredValidator",
        },
      ],
    },
    {
      id: "lastName",
      label: "Last name",
      inputType: "string",
      validators: [
        {
          type: "RequiredValidator",
        },
      ],
    },
    {
      id: "email",
      label: "Email",
      inputType: "email",
      validators: [
        {
          type: "RequiredValidator",
        },
      ],
    },
    {
      id: "phoneNumber",
      label: "Phone number",
      inputType: "string",
      validators: [
        {
          type: "RequiredValidator",
        },
      ],
    },
    {
      id: "expiration",
      label: "Expiration",
      inputType: "string",
      validators: [
        {
          type: "RequiredValidator",
        },
        {
          type: "ExpiryValidator",
        },
      ],
    },
    {
      id: "nameOnCard",
      label: "Name on card",
      inputType: "string",
      validators: [
        {
          type: "RequiredValidator",
        },
      ],
    },
  ];
  const formValidator = useYupSchema({ fields: fields });
  const { fieldProps, loading, errors, notReady } = useForm({
    initialValues: {
      billingAddress: undefined,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email || user?.contactEmail,
      phoneNumber: user?.contactNumber || "",
      nameOnCard: "",
    },
    onSubmit: cybersourceSubmit,
    validator: formValidator,
  });

  return (
    <>
      <form onSubmit={cybersourceSubmit}>
        {!riskProfile?.quotes?.[0].product?.skipPayments && (
          <HeadingLarge marginTop="scale1400" center>
            Pay via credit card
          </HeadingLarge>
        )}
        {/*
          Forcing 'focus' for easy solution to mismatching
          styles between our app and stripe elements
        */}
        <HeadingSmall>Billing details</HeadingSmall>
        <InputWrapper>
          <AddressInput
            label="Address"
            forceFocus
            error={errors?.billingAddress}
            {...fieldProps.billingAddress}
            withMap={true}
            placeholder=""
            climateDataSource="NONE"
          />
        </InputWrapper>
        <InputWrapper>
          <StringInput
            label="First name"
            forceFocus
            error={errors?.firstName}
            name="firstName"
            {...fieldProps.firstName}
          />
        </InputWrapper>
        <InputWrapper>
          <StringInput
            label="Last name"
            forceFocus
            error={errors?.lastName}
            name="lastName"
            {...fieldProps.lastName}
          />
        </InputWrapper>
        <InputWrapper>
          <EmailInput
            label="Email"
            forceFocus
            error={errors?.email}
            name="email"
            {...fieldProps.email}
          />
        </InputWrapper>

        <InputWrapper>
          <StringInput
            label="Phone number"
            forceFocus
            error={errors?.phoneNumber}
            name="phone_number"
            {...fieldProps.phoneNumber}
          />
        </InputWrapper>
        <HeadingSmall>Credit card details</HeadingSmall>
        <InputWrapper>
          <StringInput
            label="Name on card"
            forceFocus
            error={errors?.nameOnCard}
            name="nameOnCard"
            placeholder="Name"
            {...fieldProps.nameOnCard}
          />
        </InputWrapper>
        <InputWrapper>
          <FormControl label="Card number" forceFocus>
            <CybersourceField
              id="number-container"
              fieldRef={cardField}
              valid={cardValid}
              setValid={setCardValid}
            />
          </FormControl>
        </InputWrapper>
        <Block display="flex" flexDirection="row">
          <Block
            overrides={{
              Block: { style: { width: "65%", marginRight: theme.sizing.scale700 } },
            }}
          >
            <InputWrapper>
              {/* Using kanopi-components/ExpirationInput as kw/ExpirationInput has issues displaying placeholders. This issue is common to all kw Inputs */}
              <FormControl label="Expiry" forceFocus error={errors?.expiration}>
                <ExpirationInput
                  required
                  name="card_expiry"
                  {...fieldProps.expiration}
                  placeholder="MM/YY"
                  mask="99/99"
                />
              </FormControl>
            </InputWrapper>
          </Block>
          <Block>
            <InputWrapper overrides={addTestId("card-cvc-box")}>
              <FormControl label="CVC" forceFocus>
                <CybersourceField
                  id="cvc-container"
                  fieldRef={cvcField}
                  valid={cvcValid}
                  setValid={setCVCValid}
                />
              </FormControl>
            </InputWrapper>
          </Block>
        </Block>

        <Block display="flex" justifyContent="center" marginTop="scale1200">
          <Button
            type="submit"
            kind="primary"
            disabled={disableSubmit || notReady || !cardValid || !cvcValid}
            isLoading={cybersourceLoading || paymentLoading || loading}
            wide
            // TODO: Rebuild Button
            overrides={{
              BaseButton: {
                style: ({ $theme }) => ({
                  [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                    width: "100%",
                  },
                }),
              },
            }}
          >
            Pay {readableAmount}
          </Button>
        </Block>
      </form>
    </>
  );
};

export { CybersourceForm };
